<template>
    <draggable-modal @close="hadnleClose" ref="modalRef">
        <template #header>
            Profit/Loss
        </template>
        <template #body>
            <div class="flex flex-wrap pb-3">
                <div class="w-full p-0 mt-2">
                    <small><b>Note:</b> Please enter prices using the correct ‘-’ format, or enter decimal values instead.</small>
                </div>
                <div class="w-full p-0 mt-2">
                    <label for="entry">
                        Entry Price
                        <div class="error" v-if="errors.entry">{{ errors.entry }}</div>
                        <price-input :class="{'is-invalid': errors.entry}" class="form-control form-input-sm-inline" @keydown="pickPoint" ref="entryRef" id="entry" :default-value="form.entry" v-model="form.entry" :symbol-type="symbolType" :base-code="baseCode" :symbol="symbol"></price-input>
                    </label>
                </div>
                <div class="w-full p-0 mt-2">
                    <label for="exit">
                        Exit Price
                        <div class="error" v-if="errors.exit">{{ errors.exit }}</div>
                        <price-input @keydown="pickPoint" :class="{'is-invalid': errors.exit}" class="form-control form-input-sm-inline" ref="exitRef" id="exit" :default-value="form.exit" v-model="form.exit" :symbol-type="symbolType" :base-code="baseCode" :symbol="symbol"></price-input>
                    </label>
                </div>
                <div class="w-full p-0 mt-2">
                    <label for="contracts">
                        Number of Contracts
                        <input id="contracts" class="form-control form-input-sm-inline" type="text" name="contracts" v-model="form.contracts">
                    </label>
                </div>
                <div class="w-full p-0 mt-2 form-check-inline">
                    <label for="long" class="form-check-label mr-3">
                        <input class="form-check-input mr-1" id="long" type="radio" name="longOrShort" value="long" v-model="form.longOrShort" />
                        Long
                    </label>
                    <label for="short" class="form-check-label">
                        <input class="form-check-input mr-1" id="short" type="radio" name="longOrShort" value="short" v-model="form.longOrShort" />
                        Short
                    </label>
                </div>

                <div class="w-full border bg-gray-100 flex p-2 text-sm">
                    <span>
                        Profit/Loss:
                    </span>
                    <span v-if="valid" class="ml-auto" :class="getProfitLossClass()">
                        ${{ profitLoss }}
                    </span>
                </div>

                <div class="w-full p-0 mt-2">
                    <label for="prices">
                        <input id="prices" type="checkbox" name="showPrices" :value="true" v-model="form.showPrices" class="mr-1" />
                        Show Prices
                    </label>
                    <div v-if="form.showPrices" class="flex mt-2">
                        <span class="self-center text-sm mr-1">Price Lines:</span>
                        <color-picker v-model="form.color" :initial-color="form.color"></color-picker>
                    </div>
                </div>

                <div class="w-full p-0 mt-2 flex">
                    <span class="self-center text-sm mr-2">Text Color:</span>
                    <color-picker v-model="form.textColor" :initial-color="form.textColor"></color-picker>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex justify-center">
                <div v-if="isContext" class="btn btn-sm btn-red inline-flex mr-1 float-left" @click="deleteTool">Delete</div>
                <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="draw">Draw</div>
            </div>
        </template>
    </draggable-modal>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeMount, onUnmounted } from 'vue';
import ColorPicker from './ColorPicker';
import PriceInput from '../PriceInput';
import unitCodeToBaseCode from '@barchart/marketdata-api-js/lib/utilities/convert/unitCodeToBaseCode';

// Props
const props = defineProps({
    defaultValues: Object,
    defaultTraits: Object,
    symbol: String,
    // Whether this was opened from the context menu
    isContext: {
        type: Boolean,
        default: false,
        required: false
    }
});

const emit = defineEmits(['update:modelValue', 'close', 'delete', 'complete', 'pick-point']);

const modalRef = ref(null);
const form = ref(getDefaultValues());
const symbolType = window.Barchart.getFieldFromInlineTemplate('symbolType');
const baseCode = window.Barchart.getFieldFromInlineTemplate('unitCode');

const entry = ref(props.defaultValues && props.defaultValues.points[0] ? props.defaultValues.points[0] : {});
const exit = ref(props.defaultValues && props.defaultValues.points[1] ? props.defaultValues.points[1] : {});
const entryRef = ref(null);
const exitRef = ref(null);

const errors = ref({ entry: '', exit: '' });

const hadnleClose = () => {
    emit('close');
};

const unitValue = computed(() => {
    if (window.Barchart.getFieldFromInlineTemplate('symbolType') === 10) {
        const unitCode = unitCodeToBaseCode(window.Barchart.getBaseCode());
        // const unitCode = window.Barchart.Utilities.convert.unitCodeToBaseCode(window.Barchart.getBaseCode());
        return Math.pow(10, unitCode);
    }
    return 1;
});

const profitLoss = computed(() => {
    if (valid.value) {
        const direction = (form.value.longOrShort.toLowerCase() === 'short' ? 1 : -1);
        const pointValue = window.Barchart.getFieldFromInlineTemplate('pointValue');
        return ((form.value.entry - form.value.exit) * direction * form.value.contracts * pointValue * unitValue.value).toFixed(2);
    }
    return null;
});

const valid = computed(() => {
    return form.value.entry !== '' && form.value.exit !== '';
});

function getDefaultValues() {
    if (props.defaultValues) {
        return {
            entry: props.defaultValues.extras.prices[0],
            exit: props.defaultValues.extras.prices[1],
            contracts: props.defaultValues.extras.numContracts,
            longOrShort: props.defaultValues.extras.pos,
            showPrices: props.defaultValues.extras.drawLines,
            color: props.defaultValues.traits.line.color || '#000000',
            textColor: props.defaultValues.traits.fill.color || '#000000',
            lineThickness: 1,
            lineStyle: 'Solid'
        };
    } else {
        return {
            entry: '',
            exit: '',
            contracts: 1,
            longOrShort: props.defaultTraits && props.defaultTraits.longOrShort ? props.defaultTraits.longOrShort : 'long',
            showPrices: props.defaultTraits && props.defaultTraits.line && props.defaultTraits.line.width ? true : false,
            color: props.defaultTraits && props.defaultTraits.line && props.defaultTraits.line.color ? props.defaultTraits.line.color : '#000000',
            textColor: props.defaultTraits && props.defaultTraits.fill ? props.defaultTraits.fill.color : '#000000',
            lineThickness: props.defaultTraits && props.defaultTraits.line && props.defaultTraits.line.width ? props.defaultTraits.line.width : 1,
            lineStyle: 'Solid'
        }
    }
}

function pickPoint() {
    emit('pick-point', 'PickPoint');
}

function handleChartClick(eventName, event) {
    if (form.value.entry === '' || entry.value.price === '') {
        form.value.entry = event.point.price;
        entry.value = event.point;
        pickPoint();
    } else if (form.value.exit === '' || exit.value.price === '') {
        form.value.exit = event.point.price;
        exit.value = event.point;
    }
}

const getProfitLossClass = () => {
    if (profitLoss.value > 0) {
        return 'text-up';
    } else if (profitLoss.value < 0) {
        return 'text-down';
    } else {
        return 'text-unch';
    }
};

const validate = () => {
    let valid = true;

    if (form.value.entry === '') {
        errors.value.entry = 'Please enter an entry value';
        valid = false;
    }

    if (form.value.exit === '') {
        errors.value.exit = 'Please enter an exit value';
        valid = false;
    }

    return valid;
};

function deleteTool() {
    emit('close');
    emit('delete');
}

const draw = () => {
    if (!validate()) {
        return;
    }

    // If the user entered the values manually, we need to manually set the correct values
    if (Object.keys(entry.value).length === 0 || entry.value.price !== form.value.entry) {
        entry.value = {
            price: form.value.entry,
            time: new Date()
        };
    }

    if (Object.keys(exit.value).length === 0 || exit.value.price !== form.value.exit) {
        exit.value = {
            price: form.value.exit,
            time: new Date()
        };
    }

    const localTraits = {
        longOrShort: form.value.longOrShort,
        fill: { color: form.value.textColor }
    };

    if (form.value.showPrices) {
        localTraits.line = {
            color: form.value.color,
            width: form.value.lineThickness,
            dashStyle: form.value.lineStyle
        };
    }

    window.localStorage.setItem('CalculateProfitLoss', JSON.stringify(localTraits));

    const entryString = entryRef.value.formatString(form.value.entry);
    const exitString = exitRef.value.formatString(form.value.exit);

    let points = [];
    if (props.defaultValues && props.defaultValues.points && props.defaultValues.points.length === 4) {
        points = props.defaultValues.points;
        points[2].price = form.value.entry;
        points[3].price = form.value.exit;
    } else {
        points = [
            entry.value,
            exit.value
        ];
    }

    emit('complete', 'ComputedText',
        {
            line: {
                color: form.value.color,
                width: form.value.lineThickness,
                dashStyle: form.value.lineStyle,
            },
            fill: {
                color: form.value.textColor
            }
        },
        {
            points,
            prices: [
                form.value.entry,
                form.value.exit
            ],
            drawLines: form.value.showPrices,
            textLines: [
                `Entry Price: ${entryString}`,
                `Exit Price: ${exitString}`,
                `# Contracts: ${form.value.contracts}`,
                form.value.longOrShort === 'short' ? 'Short' : 'Long',
                `${profitLoss.value > 0 ? 'Profit' : 'Loss'}: $${profitLoss.value}`
            ],
            kind: 'ProfitLoss',
            numContracts: form.value.contracts,
            pos: form.value.longOrShort
        },
        points
    )

    emit('close');
};

function setupClickEvent (){
    const charts = window.Barchart.RealtimeWidgets.Charts;
    charts.PubSub.subscribe(charts.Topics.CH_ANNPOINTPICKED, handleChartClick);
}

onMounted(() => {
    setupClickEvent();
});

onUnmounted(() => {
    const charts = window.Barchart.RealtimeWidgets.Charts;
    charts.PubSub.unsubscribe(charts.Topics.CH_ANNPOINTPICKED, handleChartClick);
});
</script>


<style lang="scss">
.modalToolProfitLoss {
    // .modal-align {
    //     left: 50%;
    //     margin-left: -13rem;
    //     @screen xs {
    //         left: auto;
    //         margin-left: 0;
    //     }
    // }
    .modal-container {
        width: 24rem;
        @screen xs {
            width: 100%;
        }
    }
}
</style>
